import type { BasketFragment } from '@/generated/graphql'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Box, Collapse, Stack, Typography } from '@mui/material'

import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { UserBasketSettings } from '../../../common/domain/user-basket-settings'
import type { MenuVendor } from '../../domain/menu-vendor'
import {
  getReorderBasketState,
  useMixpanelLoadPastOrder,
} from '../../hooks/useMixpanelLoadPastOrder'
import { useCartModals, useCartReorder, useCartReorderActions } from '../../store/cartStore'
import { ReorderCard } from './reorder-card'
import { ReorderConfirmModal } from './reorder-confirm-modal'
import { usePastOrders, type PastOrderForReorder } from './use-past-orders'
import { useReorderLoader } from './use-reorder-loader'

interface ReorderDrawerProps {
  userId: string | undefined
  vendor: MenuVendor
  activeBasket: BasketFragment | null | undefined
  /** Delivery/headcount context for the 'Load Past Order' Mixpanel event. */
  orderSettings: UserBasketSettings
  currencySymbol: string
  taxAcronym: string
  /** True when the active basket is a standing-order template — reorder mutations need the flag so the server edits the template, not a one-off basket. */
  isStandingOrderTemplate: boolean
  /** Called when the user clicks Load with no active basket — should kick off the create-basket flow (settings modal). */
  onRequestCreateBasket: () => void
}

export const ReorderDrawer: React.FC<ReorderDrawerProps> = ({
  userId,
  vendor,
  activeBasket,
  orderSettings,
  currencySymbol,
  taxAcronym,
  isStandingOrderTemplate,
  onRequestCreateBasket,
}) => {
  const [open, setOpen] = useState(false)
  const [pendingOrder, setPendingOrder] = useState<PastOrderForReorder | null>(null)
  const { t } = useTranslation()
  const { loadState, queuedForNewBasket } = useCartReorder()
  const { orderDetailsModal } = useCartModals()
  const { setQueuedForNewBasket, setReorderLoadState } = useCartReorderActions()
  const { loadOrder } = useReorderLoader({
    isStandingOrderTemplate,
    editSessionId: activeBasket?.editSessionId ?? null,
  })
  const { trackLoadPastOrder } = useMixpanelLoadPastOrder({ vendor, orderSettings })

  // Clear a stale queue from a different vendor — happens when the user
  // queued a reorder on vendor A, dismissed the settings modal, and navigated
  // to vendor B without ever creating a basket.
  useEffect(() => {
    if (queuedForNewBasket && queuedForNewBasket.vendorId !== vendor.id) {
      setQueuedForNewBasket(null)
    }
  }, [queuedForNewBasket, vendor.id, setQueuedForNewBasket])

  // Clear the queue if the user abandons the basket-creation modal. We open
  // the modal via onRequestCreateBasket immediately after queuing. If the
  // modal closes while still no active basket exists, the user cancelled
  // (any actual basket creation arrives as an activeBasket transition, which
  // the basket-transition effect below handles).
  const modalWasOpenRef = useRef(false)
  useEffect(() => {
    const queuedForThisVendor = queuedForNewBasket && queuedForNewBasket.vendorId === vendor.id
    const isOpen = orderDetailsModal.open
    if (queuedForThisVendor && isOpen) {
      modalWasOpenRef.current = true
      return
    }
    if (queuedForThisVendor && !isOpen && modalWasOpenRef.current && !activeBasket) {
      // Modal closed without producing a basket — drop the stale queue so a
      // later unrelated basket doesn't replay this reorder silently.
      setQueuedForNewBasket(null)
    }
    if (!isOpen) {
      modalWasOpenRef.current = false
    }
  }, [orderDetailsModal.open, queuedForNewBasket, vendor.id, activeBasket, setQueuedForNewBasket])

  const {
    orders,
    loading: pastOrdersLoading,
    error: pastOrdersError,
  } = usePastOrders({ userId, vendorId: vendor.id })

  // Watch for basket transitioning from absent to present while a reorder is queued.
  // The basket-creation mutation can resolve before the past-orders list query,
  // so we wait until past orders have finished loading before deciding the queued
  // order is missing — otherwise we'd silently drop the reorder.
  const sawBasketRef = useRef(false)
  useEffect(() => {
    const currentBasket = activeBasket ?? null
    const hadBasket = sawBasketRef.current

    // Only act on queues that were created for this vendor.
    const queuedOrderId =
      queuedForNewBasket && queuedForNewBasket.vendorId === vendor.id
        ? queuedForNewBasket.orderId
        : null

    if (!queuedOrderId) {
      sawBasketRef.current = Boolean(currentBasket)
      return
    }
    if (hadBasket || !currentBasket) {
      sawBasketRef.current = Boolean(currentBasket)
      return
    }
    if (pastOrdersLoading) {
      // Past orders still loading — leave sawBasketRef as false so this effect
      // re-evaluates once the query resolves.
      return
    }

    sawBasketRef.current = true

    const queuedOrder = orders.find((o) => o.id === queuedOrderId)
    if (!queuedOrder) {
      setQueuedForNewBasket(null)
      return
    }

    setQueuedForNewBasket(null)
    void loadOrder({ order: queuedOrder, activeBasket: currentBasket })
    setOpen(false)
  }, [
    activeBasket,
    queuedForNewBasket,
    vendor.id,
    orders,
    pastOrdersLoading,
    loadOrder,
    setQueuedForNewBasket,
  ])

  // Hide the drawer entirely only when there are no past orders AND no error
  // to surface — otherwise we render an error state so the user knows the
  // feature was attempted (and how to retry).
  if (orders.length === 0 && !pastOrdersError) return null

  const isBusy = loadState === 'loading' || loadState === 'confirming'

  const handleLoadClick = (order: PastOrderForReorder) => {
    if (isBusy) return
    // Tracked before the branch — the event records the intent, not the outcome.
    trackLoadPastOrder({ order, basketState: getReorderBasketState(activeBasket) })
    if (!activeBasket) {
      // Queue the load, then kick off basket creation. The useEffect above will
      // replay the order once the basket exists.
      setQueuedForNewBasket({ orderId: order.id, vendorId: vendor.id })
      onRequestCreateBasket()
      return
    }
    if (activeBasket.items.length === 0) {
      void loadOrder({ order, activeBasket })
      setOpen(false)
    } else {
      setPendingOrder(order)
      setReorderLoadState('confirming', order.id)
    }
  }

  const handleCancelConfirm = () => {
    setPendingOrder(null)
    setReorderLoadState('idle', null)
  }

  const handleConfirm = () => {
    if (!pendingOrder || !activeBasket) return
    const orderToLoad = pendingOrder
    setPendingOrder(null)
    setReorderLoadState('idle', null)
    void loadOrder({ order: orderToLoad, activeBasket })
    setOpen(false)
  }

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        component="button"
        onClick={() => setOpen((v) => !v)}
        sx={{
          width: '100%',
          padding: '10px 12px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          transition: 'background .15s',
          '&:hover': {
            bgcolor: (theme) => theme.palette.action.hover,
            borderRadius: '12px',
          },
        }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box
            sx={{
              width: 30,
              height: 30,
              bgcolor: 'primary.main',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
            <RefreshIcon sx={{ fontSize: 16, color: 'common.white' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }} data-cy="reorder-from-past-orders">
              {t('marketplace:reorderFromPast')}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {t('marketplace:loadItemsFromPreviousOrder')}
            </Typography>
          </Box>
        </Stack>
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: 'text.disabled',
            transition: 'transform .2s',
            transform: open ? 'rotate(180deg)' : 'none',
            flexShrink: 0,
          }}
        />
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ padding: 1, maxHeight: 252, overflowY: 'auto' }}>
          {pastOrdersError && orders.length === 0 ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', padding: 1, color: 'error.main' }}>
              {t('marketplace:reorderLoadError')}
            </Typography>
          ) : null}
          <Stack spacing={0.5}>
            {orders.map((order) => (
              <ReorderCard
                key={order.id}
                order={order}
                onLoad={() => handleLoadClick(order)}
                disabled={isBusy}
                currencySymbol={currencySymbol}
                taxAcronym={taxAcronym}
              />
            ))}
          </Stack>
        </Box>
      </Collapse>

      <ReorderConfirmModal
        open={pendingOrder !== null}
        onCancel={handleCancelConfirm}
        onConfirm={handleConfirm}
      />
    </Box>
  )
}
